import React, { createContext, useContext, useState, useCallback } from 'react';

const PrivacyContext = createContext();

// djb2 hash — deterministic, same input always produces same output
const hashString = (str) => {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
        hash = hash >>> 0;
    }
    return hash;
};

const ADJECTIVES = [
    'Wise', 'Swift', 'Bold', 'Calm', 'Bright', 'Brave', 'Keen', 'Eager',
    'Cool', 'Fuzzy', 'Sneaky', 'Happy', 'Mystic', 'Silent', 'Wild',
    'Fluffy', 'Tiny', 'Giant', 'Lucky', 'Cosmic', 'Gentle', 'Clever',
    'Nimble', 'Merry', 'Curious', 'Sunny', 'Quiet', 'Lively', 'Shy',
    'Daring', 'Noble', 'Playful', 'Rapid', 'Radiant', 'Serene', 'Witty',
    'Agile', 'Cheerful', 'Fearless', 'Graceful', 'Jolly', 'Vivid',
    'Humble', 'Mighty', 'Dreamy', 'Zesty',
];

const ANIMALS = [
    'Owl', 'Fox', 'Bear', 'Wolf', 'Eagle', 'Lion', 'Tiger', 'Panda',
    'Rabbit', 'Penguin', 'Dolphin', 'Koala', 'Giraffe', 'Zebra', 'Elephant',
    'Flamingo', 'Raccoon', 'Hedgehog', 'Platypus', 'Capybara',
    'Narwhal', 'Axolotl', 'Sloth', 'Llama', 'Gecko', 'Otter', 'Falcon',
    'Badger', 'Bison', 'Moose', 'Deer', 'Seal', 'Walrus', 'Leopard',
    'Cheetah', 'Jaguar', 'Panther', 'Swan', 'Crane', 'Heron', 'Turtle',
    'Tortoise', 'Octopus', 'Seahorse', 'Mantis', 'Meerkat', 'Tapir',
    'Wombat', 'Quokka', 'Alpaca',
];

const TEAM_ADJECTIVES = [
    'Deep', 'Wild', 'Frozen', 'Sunlit', 'Ancient', 'Hidden',
    'Vast', 'Dark', 'Misty', 'Rugged', 'Lush', 'Barren',
    'Crystal', 'Burning', 'Silent',
];

const TEAM_HABITATS = [
    'Tundra', 'Savanna', 'Glacier', 'Forest', 'Canyon', 'Lagoon',
    'Jungle', 'Prairie', 'Bayou', 'Delta', 'Summit', 'Ridge',
    'Reef', 'Basin', 'Hollow',
];

export function PrivacyProvider({ children }) {
    const [isPrivacyMode, setIsPrivacyMode] = useState(false);

    const togglePrivacyMode = useCallback(() => setIsPrivacyMode(prev => !prev), []);

    const maskName = useCallback((real) => {
        if (!isPrivacyMode || !real) return real;
        const adj = ADJECTIVES[hashString(real) % ADJECTIVES.length];
        const animal = ANIMALS[hashString(real + '\x01') % ANIMALS.length];
        return `${adj} ${animal}`;
    }, [isPrivacyMode]);

    const maskEmail = useCallback((real) => {
        if (!isPrivacyMode || !real) return real;
        return `student${(hashString(real) % 9999) + 1}@uci.edu`;
    }, [isPrivacyMode]);

    const maskAdminEmail = useCallback((real) => {
        if (!isPrivacyMode || !real) return real;
        return `admin${(hashString(real) % 99) + 1}@uci.edu`;
    }, [isPrivacyMode]);

    const maskGroupName = useCallback((real) => {
        if (!isPrivacyMode || !real) return real;
        const adj = TEAM_ADJECTIVES[hashString(real) % TEAM_ADJECTIVES.length];
        const habitat = TEAM_HABITATS[hashString(real + '\x01') % TEAM_HABITATS.length];
        return `${adj} ${habitat}`;
    }, [isPrivacyMode]);

    // id preferred (unique DB key)
    const maskCourseName = useCallback((name, id) => {
        if (!isPrivacyMode || !name) return name;
        const key = id != null ? String(id) : name;
        return `Project ${(hashString(key) % 50) + 1}`;
    }, [isPrivacyMode]);

    // Returns 1-2 letter initial derived from the masked name
    const maskInitial = useCallback((realName) => {
        if (!isPrivacyMode || !realName) return null;
        const masked = maskName(realName);
        const parts = masked.trim().split(/\s+/);
        if (parts.length >= 2) return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
        return parts[0].charAt(0).toUpperCase();
    }, [isPrivacyMode, maskName]);

    return (
        <PrivacyContext.Provider value={{ isPrivacyMode, togglePrivacyMode, maskName, maskEmail, maskAdminEmail, maskGroupName, maskCourseName, maskInitial }}>
            {children}
        </PrivacyContext.Provider>
    );
}

export function usePrivacy() {
    return useContext(PrivacyContext);
}
